import { useEffect, useRef, useState } from 'react';

import {
  faThumbsDown,
  faThumbsUp,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faThumbsDown as faThumbsDownSolid,
  faThumbsUp as faThumbsUpSolid,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLingui } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';

import { FeedbackDialog } from './FeedbackDialog';
import type { FeedbackOption, MiddleAction } from './types';

interface FeedbackWidgetProps {
  upOptions: FeedbackOption[];
  downOptions: FeedbackOption[];
  label?: React.ReactNode;
  className?: string;
  variant?: 'default' | 'minimalist';
  middleAction?: MiddleAction;
  existingRating?: 'up' | 'down'; // Existing rating from backend ('up' = positive, 'down' = negative)
  onRate: (
    rating: 'up' | 'down',
    selectedOptions: string[],
    additionalComments?: string,
  ) => void;
  onRatingButtonClick?: (rating: 'up' | 'down') => void;
}

export const FeedbackWidget = ({
  className = '',
  label = '',
  upOptions,
  downOptions,
  variant = 'default',
  middleAction,
  existingRating,
  onRate,
  onRatingButtonClick,
}: FeedbackWidgetProps) => {
  // state - initialize with existingRating if provided
  const [rating, setRating] = useState<'up' | 'down' | null>(
    existingRating ?? null,
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFeedbackOptions, setSelectedFeedbackOptions] = useState<
    string[]
  >([]);
  const [pendingRating, setPendingRating] = useState<'up' | 'down' | null>(
    null,
  );

  // Track the last synced existingRating to prevent overriding user-initiated changes
  const lastSyncedRatingRef = useRef<'up' | 'down' | undefined>(existingRating);

  // hooks
  const { t } = useLingui();

  // handlers
  const handleRate = (newRating: 'up' | 'down') => {
    onRatingButtonClick?.(newRating);
    setPendingRating(newRating);
    setIsDialogOpen(true);
    setSelectedFeedbackOptions([]);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setPendingRating(null);
      setSelectedFeedbackOptions([]);
    }
  };

  // Sync rating state with existingRating prop only when it changes externally
  // This prevents overriding user-initiated changes that haven't been reflected in the prop yet
  useEffect(() => {
    // Only sync if existingRating has actually changed from the last synced value
    if (existingRating !== lastSyncedRatingRef.current) {
      setRating(existingRating ?? null);
      lastSyncedRatingRef.current = existingRating;
    }
  }, [existingRating]);

  return (
    <div className={`vai-flex vai-flex-col ${className}`}>
      <div className="vai-flex vai-items-center vai-gap-2">
        {/* Label */}
        {label && (
          <span className="vai-text-sm vai-text-gray-700">{label}</span>
        )}
        <div className={`vai-flex ${variant === 'default' ? 'vai-gap-1' : ''}`}>
          {/* Up button */}
          <Button
            variant={variant === 'minimalist' ? 'ghost' : 'outline'}
            size={variant === 'minimalist' ? 'sm' : 'default'}
            title={t`Rate positively`}
            onClick={() => handleRate('up')}
            data-testid="feedback-up-button"
            className={
              variant === 'minimalist' ? 'vai-max-w-8 vai-max-h-8' : ''
            }
          >
            <FontAwesomeIcon
              icon={rating === 'up' ? faThumbsUpSolid : faThumbsUp}
              className="vai-text-muted-foreground"
            />
          </Button>
          {/* Optional middle action button */}
          {middleAction && (
            <Button
              variant="ghost"
              size={variant === 'minimalist' ? 'sm' : 'default'}
              title={middleAction.title}
              disabled={middleAction.isDisabled}
              onClick={middleAction.onClick}
              className={
                variant === 'minimalist' ? 'vai-max-w-8 vai-max-h-8' : ''
              }
            >
              <div className="vai-text-muted-foreground">
                {middleAction.icon}
              </div>
            </Button>
          )}
          {/* Down button */}
          <Button
            variant={variant === 'minimalist' ? 'ghost' : 'outline'}
            size={variant === 'minimalist' ? 'sm' : 'default'}
            title={t`Rate negatively`}
            onClick={() => handleRate('down')}
            data-testid="feedback-down-button"
            className={
              variant === 'minimalist' ? 'vai-max-w-8 vai-max-h-8' : ''
            }
          >
            <FontAwesomeIcon
              icon={rating === 'down' ? faThumbsDownSolid : faThumbsDown}
              className="vai-text-muted-foreground"
            />
          </Button>
        </div>
      </div>

      {/* Feedback dialog */}
      <FeedbackDialog
        initialSelectedOptions={selectedFeedbackOptions}
        rating={pendingRating}
        upOptions={upOptions}
        downOptions={downOptions}
        isOpen={isDialogOpen}
        onRate={(dialogRating, options, comments) => {
          setRating(dialogRating);
          setPendingRating(null);
          // Update ref to match the new rating so we don't override it when existingRating updates
          lastSyncedRatingRef.current = dialogRating;
          onRate(dialogRating, options, comments);
        }}
        onOpenChange={handleDialogOpenChange}
      />
    </div>
  );
};
